import React from "react";
import { howItWorks } from "../content/landingContent";
import { Reveal } from "./Reveal";
import { WhatsAppChatMock } from "./WhatsAppChatMock";
import { useContentField, useContentJson } from "../lib/useContent";
import { useResourceImage } from "../lib/useResourceImage";
import step1Image from "../assets/how-it-works/paso-01.jpg";
import step2Image from "../assets/how-it-works/paso-02.jpg";
import step3Image from "../assets/how-it-works/paso-03.jpg";

export const HowItWorks: React.FC = () => {
  const title = useContentField("howItWorks.title", howItWorks.title);
  const subtitle = useContentField("howItWorks.subtitle", howItWorks.subtitle);
  const steps = useContentJson("howItWorks.stepsJson", howItWorks.steps);

  const { src: step1Src } = useResourceImage("step-1", step1Image);
  const { src: step2Src } = useResourceImage("step-2", step2Image);
  const { src: step3Src } = useResourceImage("step-3", step3Image);

  const stepImages = [step1Src, step2Src, step3Src];
  const step4 = steps[3];
  return (
    <section
      id="como-funciona"
      data-testid="how-it-works-section"
      className="bg-white py-20 sm:py-24 lg:py-28"
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <Reveal className="max-w-2xl">
          <span className="font-display text-xs font-semibold uppercase tracking-[0.2em] text-motor-red">
            Capítulo 04
          </span>
          <h2 className="mt-4 font-display font-bold text-3xl sm:text-4xl lg:text-[2.75rem] text-motor-ink leading-tight">
            {title}
          </h2>
          <p className="mt-4 text-base text-neutral-600 leading-relaxed">{subtitle}</p>
        </Reveal>

        <div className="mt-14 grid lg:grid-cols-2 gap-10 lg:gap-6">
          {steps.slice(0, 3).map((step, idx) => (
            <Reveal key={step.number} delay={idx * 0.08} className={idx === 2 ? "lg:col-span-2" : ""}>
              <div
                data-testid={`how-it-works-step-${step.number}`}
                className={`h-full rounded-2xl border border-neutral-200 p-6 sm:p-7 flex ${
                  idx === 2 ? "flex-col sm:flex-row" : "flex-col"
                } gap-6`}
              >
                <div className={idx === 2 ? "sm:w-1/3" : ""}>
                  <div className="overflow-hidden rounded-xl bg-neutral-100 aspect-[4/3]">
                    <img
                      src={stepImages[idx]}
                      alt={`Paso ${step.number}: ${step.title}`}
                      className="h-full w-full object-cover"
                      loading="lazy"
                    />
                  </div>
                </div>
                <div className="flex-1">
                  <span className="font-display font-extrabold text-3xl text-motor-red/25">
                    {step.number}
                  </span>
                  <h3 className="mt-2 font-display font-semibold text-lg text-motor-ink">
                    {step.title}
                  </h3>
                  <p className="mt-2 text-sm text-neutral-600 leading-relaxed">{step.text}</p>
                </div>
              </div>
            </Reveal>
          ))}

          <Reveal delay={0.24}>
            <div
              data-testid="how-it-works-step-04"
              className="h-full rounded-2xl border border-neutral-200 p-6 sm:p-7 grid sm:grid-cols-2 gap-6 items-center"
            >
              {step4 && (
              <div>
                <span className="font-display font-extrabold text-3xl text-motor-red/25">
                  {step4.number}
                </span>
                <h3 className="mt-2 font-display font-semibold text-lg text-motor-ink">
                  {step4.title}
                </h3>
                <p className="mt-2 text-sm text-neutral-600 leading-relaxed">
                  {step4.text}
                </p>
              </div>
              )}
              <WhatsAppChatMock />
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
